import { Checkbox, Col, PageHeader, Row, TableColumnsType } from 'antd';
import { Button, Table } from 'components';
import { dayjs } from 'libs';
import { chain, join } from 'lodash';
import {
  Children,
  FC,
  Fragment,
  Key,
  PropsWithChildren,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { generatePath, useHistory } from 'react-router-dom';
import { GetApproved } from 'types';
import { routes } from 'navigations/routers';
import { useDispatch, useSelector } from 'react-redux';
import { getStatusApproved } from 'pages/SiteDetail/thunk';
import { postSiteApprove } from '../thunk';
import { removeApproved } from '../../../SiteDetail/slice';
import { siteDetailSelector, siteDetailSelectors } from '../../../SiteDetail/selectors';
import ApproveRY from './ApproveRY';

import SectionStyled from './styles';
import ApproveRKY from './ApproveRKY';

type Props = {};

const UnapprovedSites: FC<Props> = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const approved = useSelector(siteDetailSelectors.selectAll);
  const {
    loadingApproved,
    // 2024/10/30 Leading Win エラーメッセージ出力表示の対応 DXWEBLOS00200_現場一覧（未承認） start
    error_message,
    // 2024/10/30 Leading Win エラーメッセージ出力表示の対応 DXWEBLOS00200_現場一覧（未承認） end
  } = useSelector(siteDetailSelector);
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
  const [visibleApproveRKY, setVisibleApproveRKY] = useState<boolean>(false);

  const rowSelection = useMemo(
    () => ({
      selectedRowKeys,
      onChange: (rowKeys: Key[]) => setSelectedRowKeys(rowKeys),
    }),
    [selectedRowKeys]
  );

  const checked = useMemo(
    () => selectedRowKeys.length === approved.length,
    [approved.length, selectedRowKeys.length]
  );
  const dataSource = useMemo(
    () =>
      approved.map((site) => ({
        ...site,
        date: dayjs(site.date).format('YYYY/MM/DD'),
      })),
    [approved]
  );

  const handleCheck = useCallback(() => {
    if (selectedRowKeys.length === approved.length) {
      return setSelectedRowKeys([]);
    }
    setSelectedRowKeys(approved.map(({ id }) => id));
  }, [approved, selectedRowKeys.length]);

  const rowKey = useCallback(({ id }) => id, []);

  const handleRow = useCallback(
    ({ building_id, site_id, date }) => {
      return {
        onClick: () =>
          history.push(
            generatePath(routes.SiteDetailN.path, {
              buildingId: building_id,
              site_id,
              rky_created_date: date.replace(/\//g, '-'),
            })
          ),
      };
    },
    [history]
  );

  const handleCheckApprove = useCallback(async () => {
    try {
      const newData: { building_id: number; date: string; cooperator_id: string }[] = [];
      // 2026/02/24 彭　課題No.153 一括承認時名監督API呼出し問題対応 start
      const removedIds: number[] = [];
      // ❌ 旧コード（選んだ行ごとに承認API・未承認一覧取得を繰り返していたため、一括で集約して1回ずつ実行に変更）
      // for (let index = 0; index < approved.length; index++) {
      //   if (selectedRowKeys.findIndex((item) => item === approved[index].id) !== -1) {
      //     if (approved[index] && approved.length > 0) {
      //       approved[index].checked_cooperators.forEach((items) => {
      //         newData.push({
      //           building_id: approved[index].building_id,
      //           date: approved[index].date,
      //           cooperator_id: items.cooperator_id.toString(),
      //         });
      //       });
      //     }
      //     await dispatch(
      //       postSiteApprove({
      //         type: '',
      //         status: 2,
      //         ky_informations: newData,
      //       })
      //     );
      //     await dispatch(removeApproved({ ids: [approved[index].id] }));
      //     dispatch(getStatusApproved({}));
      //   }
      // }
      for (let index = 0; index < approved.length; index++) {
        if (selectedRowKeys.findIndex((item) => item === approved[index].id) === -1) continue;
        if (!approved[index]?.checked_cooperators?.length) continue;
        approved[index].checked_cooperators.forEach((items) => {
          newData.push({
            building_id: approved[index].building_id,
            date: approved[index].date,
            cooperator_id: items.cooperator_id.toString(),
          });
        });
        removedIds.push(approved[index].id);
      }
      if (newData.length === 0) return;
      await dispatch(
        postSiteApprove({
          type: '',
          status: 2,
          ky_informations: newData,
        })
      );
      await dispatch(removeApproved({ ids: removedIds }));
      dispatch(getStatusApproved({}));
      // 2026/02/24 彭　課題No.153 一括承認時名監督API呼出し問題対応 end
    } catch (e) {
      console.log(e);
    }
  }, [approved, dispatch, selectedRowKeys]);

  const columns: TableColumnsType<GetApproved> = useMemo(
    () => [
      {
        title: 'RKY作成日',
        dataIndex: 'date',
        defaultSortOrder: 'descend',
        width: '129px',
        sortDirections: ['descend', 'ascend'],
        render: (date: string) => dayjs(date).format('YYYY/MM/DD'),
      },
      {
        title: '工事コード',
        width: '25%',
        className: 'boder-left',
        render: (value: GetApproved) => {
          return (value?.building_code).substring(0, (value?.building_code).lastIndexOf('-'));
        },
      },
      {
        title: '現場名',
        width: '20%',
        className: 'boder-left',
        render: (value: GetApproved) => {
          return value?.site_name;
        },
      },
      {
        title: '確認済み業者',
        className: 'boder-left',
        render: (value: GetApproved) => {
          return join(
            value?.checked_cooperators.map((i) => i.cooperator_name),
            ','
          );
        },
      },
    ],
    []
  );

  return (
    <SectionStyled>
      <ApproveRKY
        title={
          selectedRowKeys.length === approved.length
            ? '選択されたRKYを一括承認します。よろしいですか？'
            : 'RKY表を承認します。よろしいですか？'
        }
        visibleApproveRKY={visibleApproveRKY}
        onCancel={() => {
          setVisibleApproveRKY(false);
          setSelectedRowKeys([]);
        }}
        onOk={() => {
          setVisibleApproveRKY(false);
          handleCheckApprove();
          setSelectedRowKeys([]);
        }}
      />
      <PageHeader
        className="site-page-header"
        title={
          <Checkbox
            onChange={handleCheck}
            checked={!loadingApproved && checked}
            disabled={!(dataSource.length > 0)}
          >
            全ての現場を選択
          </Checkbox>
        }
        extra={[
          <Button
            size="small"
            key={0}
            onClick={() => setVisibleApproveRKY(true)}
            disabled={!selectedRowKeys.length}
          >
            RKYの一括承認
          </Button>,
        ]}
      />
      <Row>
        <Col span={24} className="table-content">
          {/* 2024/10/30 Leading Win エラーメッセージ出力表示の対応 DXWEBLOS00200_現場一覧（未承認） start */}
          {error_message && <p className="error-message">{error_message}</p>}
          {/* 2024/10/30 Leading Win エラーメッセージ出力表示の対応 DXWEBLOS00200_現場一覧（未承認） end */}
          <Table
            loading={loadingApproved}
            columns={columns}
            dataSource={dataSource}
            components={{ body: { wrapper: Wrapper } }}
            rowSelection={rowSelection}
            rowKey={rowKey}
            onRow={handleRow}
          />
        </Col>
      </Row>
    </SectionStyled>
  );
};

const Wrapper: FC<PropsWithChildren<{ className: string }>> = ({ className, children }) => {
  const groupRows = useMemo(
    () =>
      chain(Children.toArray(children))
        .filter((child: any) => child.props && child.props.record)
        .groupBy((child: any) => {
          const rec = child.props.record as GetApproved;
          const prefix = rec.building_code.slice(0, rec.building_code.lastIndexOf('-'));
          return `${rec.date}-${prefix}`;
        })
        .map((value, key) => ({ site_director_approved_at: key, children: value }))
        .value(),
    [children]
  );

  return (
    <>
      {groupRows.map((groupRow, index) => (
        <Fragment key={index}>
          <tbody className={`${className} ant-tbody-group`} children={groupRow.children[0]} />
          <tbody className="separate" />
        </Fragment>
      ))}
    </>
  );
};

export default UnapprovedSites;
